import { HttpTypes } from '@/types';
import { StoreCreateShippingRatesResponse, StoreOrderCreateResponse } from '@/types/http';
import {
  BaseOrderDetailItem,
  BaseShippingInfoPayload,
  BaseShippingRatesPayload,
  PromotionResponse,
  TExternalOrder,
} from '@/types/http/orders/common';
import { AxiosInstance, AxiosRequestConfig } from 'axios';

class Cart {
  private client: AxiosInstance;
  private orderPath = '/orders/external/order';
  private sitesPath = '/sites';

  public host = '';

  constructor(client: AxiosInstance) {
    client.interceptors.request.use();

    this.client = client;
  }

  interceptRequest(config: AxiosRequestConfig) {
    config.headers = {
      ...config.headers,
    };

    return config;
  }

  async detail(
    id: string,
    token: string
  ): Promise<HttpTypes.IResponse<HttpTypes.StoreCustomerOrderDetail>> {
    const result = await this.client.get(`${this.orderPath}/detail/${id}`, {
      headers: {
        Origin: this.host,
        Authorization: `Bearer ${token}`,
      },
    });
    return result.data;
  }

  async successPayment(id: string): Promise<
    HttpTypes.IResponse<{
      order_no: string;
      items: BaseOrderDetailItem[];
      grand_total: number;
    }>
  > {
    const result = await this.client.get(`${this.orderPath}/detail/${id}/success`, {
      headers: {
        Origin: this.host,
      },
    });
    return result.data;
  }

  async create(
    data: TExternalOrder,
    token?: string,
    locationId?: string
  ): Promise<HttpTypes.IResponse<StoreOrderCreateResponse>> {
    const headers = {
      Origin: this.host,
      'X-Location-Id': locationId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.post(`${this.orderPath}`, data, {
      headers,
    });
    return result.data;
  }

  async updateCart(cartId: string, locationId?: string, token?: string) {
    const headers = {
      Origin: this.host,
      'X-Location-Id': locationId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const result = await this.client.post(
      `${this.sitesPath}/cart/checkout`,
      { cartId },
      {
        headers,
      }
    );
    return result.data;
  }

  async listArea(queryParams: HttpTypes.FindParams): Promise<HttpTypes.StoreAreaResponse> {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.get('/sites/shipping/area', {
      params: queryParams,
      headers: {
        Origin: this.host,
        // 'X-Jstore-Host': this.host,
      },
    });
    return result.data;
  }

  async listShippingCategory(): Promise<HttpTypes.StoreShippingCategoryResponse> {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.get('/sites/shipping/service-category', {
      headers: {
        Origin: this.host,
      },
    });
    return result.data;
  }

  async createRates(
    data: BaseShippingRatesPayload,
    locationId?: string
  ): Promise<HttpTypes.IResponse<StoreCreateShippingRatesResponse>> {
    const result = await this.client.post('/sites/shipping/rates', data, {
      headers: {
        Origin: this.host,
        'X-Location-Id': locationId as string,
      },
    });
    return result.data;
  }

  async updateRates(data: HttpTypes.StoreUpdateShippingRates, locationId?: string) {
    this.interceptRequest({ ...this.client.request });

    const result = await this.client.post(`${this.sitesPath}/cart/shipping/update`, data, {
      headers: {
        Origin: this.host,
        'X-Location-Id': locationId as string,
      },
    });
    return result.data;
  }

  async updateShippingInfo(
    checkoutId: string,
    data: BaseShippingInfoPayload
  ): Promise<HttpTypes.IResponse<{ hash: string }>> {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.post(
      `${this.sitesPath}/checkout/shipping-info/${checkoutId}`,
      data,
      {
        headers: {
          Origin: this.host,
        },
      }
    );
    return result.data;
  }

  async listLocationPickup(items: string[]): Promise<HttpTypes.StoreOrderLocationPickupResponse> {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.post(
      `${this.sitesPath}/pickup-locations`,
      {
        item_ids: items,
      },
      {
        headers: {
          Origin: this.host,
        },
      }
    );
    return result.data;
  }

  async orderSettings(): Promise<HttpTypes.StoreOrderSettingsResponse> {
    const result = await this.client.get(`${this.sitesPath}/order-setting`, {
      headers: {
        Origin: this.host,
      },
    });
    return result.data;
  }

  async updatePickupCart(data: HttpTypes.StoreUpdatePickupCart) {
    const result = await this.client.post(`${this.sitesPath}/cart/pickup/update`, data, {
      headers: {
        Origin: this.host,
      },
    });
    return result.data;
  }

  async sendNotification(orderId: string): Promise<HttpTypes.IResponse<{ status: string }>> {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.post(
      `${this.sitesPath}/pickup-notify`,
      { order_id: orderId },
      {
        headers: {
          Origin: this.host,
        },
      }
    );
    return result.data;
  }

  async getAvailableShippingPromotions(checkoutId: string): Promise<PromotionResponse> {
    this.interceptRequest({ ...this.client.request });

    const result = await this.client.get(
      `${this.sitesPath}/cart/shipping-promotions/available/${checkoutId}`, // using checkoutId as path param
      {
        headers: {
          Origin: this.host,
        },
      }
    );

    return result.data;
  }
}

export default Cart;
